import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import ChevronSmall from "../Icons/ChevronSmall";

interface KmSelectorProps {
  selectedDistancyAway: number;
  setSelectedDistancyAway: Dispatch<SetStateAction<number>>;
  isEditing?: boolean;
}

const predefinedOptions = [1, 5, 10, 15, 20];

const KmSelector: React.FC<KmSelectorProps> = ({
  selectedDistancyAway,
  setSelectedDistancyAway,
  isEditing = false,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [customInput, setCustomInput] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      toggleDropdown();
    }
  }, [isEditing]);

  useEffect(() => {
    setCustomInput("")
    setIsDropdownOpen(false)
  }, [selectedDistancyAway])

  const isCustomValue = !predefinedOptions.includes(selectedDistancyAway);

  const increment = () => setSelectedDistancyAway((prev) => prev + 1);

  const decrement = () =>
    setSelectedDistancyAway((prev) => (prev > 0 ? prev - 1 : 0));

  const toggleDropdown = () => {
    setCustomInput(isCustomValue ? String(selectedDistancyAway) : "");
    setIsDropdownOpen((prev) => !prev);
  };

  const handleOptionClick = (value: number) => {
    setSelectedDistancyAway(value);
    setIsDropdownOpen(false);
  };

  const handleCustomInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (/^\d*$/.test(val)) {
      setCustomInput(val);
    }
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target as Node)
    ) {
      if (customInput !== "") {
        const numericValue = parseInt(customInput);
        if (!isNaN(numericValue)) {
          setSelectedDistancyAway(numericValue);
        }
      }
      setIsDropdownOpen(false);
    }
  };

  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Focus input if it's a custom value
      if (isCustomValue && inputRef.current) {
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen, customInput]);

  return (
    <div
      className="d-flex align-items-center position-relative"
      ref={dropdownRef}
    >
      <div
        className="fs-13 fw-700 text-black cursor-pointer user-select-none"
        onClick={toggleDropdown}
      >
        {selectedDistancyAway}km
      </div>

      {isDropdownOpen && (
        <div
          className="dropdown-shadow position-absolute bg-white z-1 border border-grey-light radius-8px min-w-110px"
          style={{ top: "100%", right: 0 }}
        >
          <div className="p-6px">
            <div className="form-group">
              <input
                ref={inputRef}
                type="text"
                value={customInput}
                onChange={handleCustomInputChange}
                className="form-control border-grey-light"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const numericValue = parseInt(customInput);
                    if (!isNaN(numericValue)) {
                      setSelectedDistancyAway(numericValue);
                    }
                    setIsDropdownOpen(false);
                  }
                }}
                placeholder="Custom..."
              />
            </div>
          </div>
          <ul className="list-unstyled m-0">
            {predefinedOptions.map((option) => (
              <li
                key={option}
                className={`cursor-pointer hover-bg-off-white px-15px py-10px fs-16 fw-400 text-black lh-20px d-flex gap-8 text-break ${
                  selectedDistancyAway === option
                    ? "bg-teal text-black"
                    : ""
                }`}
                onClick={() => handleOptionClick(option)}
              >
                {option}km
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="d-flex flex-column">
        <button onClick={increment} className="btn-theme bg-white p-0 h-auto">
          <ChevronSmall isOpen={false} />
        </button>
        <button onClick={decrement} className="btn-theme bg-white p-0 h-auto">
          <ChevronSmall isOpen={true} />
        </button>
      </div>
    </div>
  );
};

export default KmSelector;
